import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ISSUE_TYPES, PRIORITIES } from '@joker/domain'
import { useTRPC } from '../trpc'
import { ErrorNote, Loading, Panel, ViewTitle } from '../ui'

/**
 * What needs a person to act, most urgent first. Every item comes from the
 * funnel rules in shared/domain and clears itself when the event that resolves
 * it is recorded, so nothing here is ever closed by hand.
 */
export function Todo({ onOpenPerson, onOpenOrder }: { onOpenPerson: (id: string) => void; onOpenOrder: (id: string) => void }) {
  const trpc = useTRPC()
  const q = useQuery({ ...trpc.todo.list.queryOptions(), refetchInterval: 60_000 })
  const [only, setOnly] = useState<string | null>(null)

  if (q.error) return <ErrorNote error={q.error} />

  return (
    <>
      <ViewTitle suit="spade">To do</ViewTitle>
      <p className="view-sub">
        Everything waiting on somebody here, most urgent first and oldest first within each. An item
        disappears by itself once the thing it waits for happens.
      </p>

      <details className="todo-legend">
        <summary>What counts as an issue, and why this priority</summary>
        {PRIORITIES.map((p) => (
          <div key={p.priority} className="todo-legend-group">
            <h4>
              {p.title} <span className="muted">· {p.why}</span>
            </h4>
            <ul>
              {ISSUE_TYPES.filter((t) => t.priority === p.priority).map((t) => (
                <li key={t.id}>
                  <b>{t.label}</b>: {t.rule}.
                </li>
              ))}
            </ul>
          </div>
        ))}
      </details>

      {!q.data ? (
        <Loading what="the to do list" />
      ) : (
        <>
          <div className="todo-chips" role="group" aria-label="Show one kind of issue">
            <button type="button" className={`chip-btn${only === null ? ' on' : ''}`} onClick={() => setOnly(null)}>
              All <span className="num">{q.data.items.length}</span>
            </button>
            {ISSUE_TYPES.map((t) => {
              const n = q.data.counts.find((c) => c.issue === t.id)?.count ?? 0
              if (!n) return null
              return (
                <button
                  key={t.id}
                  type="button"
                  className={`chip-btn p${t.priority}${only === t.id ? ' on' : ''}`}
                  onClick={() => setOnly(only === t.id ? null : t.id)}
                >
                  {t.label} <span className="num">{n}</span>
                </button>
              )
            })}
          </div>

          {PRIORITIES.map((p) => {
            const items = q.data.items.filter((i) => i.priority === p.priority && (!only || i.issue === only))
            return (
              <Panel key={p.priority} title={`${p.title} · ${items.length}`} note={p.why}>
                {items.length === 0 ? (
                  <p className="footnote">Nothing here.</p>
                ) : (
                  <div className="scroll-x">
                    <table className={`data todo-table todo-p${p.priority}`}>
                      <thead>
                        <tr>
                          <th>Who</th>
                          <th>What</th>
                          <th className="r">Days waiting</th>
                          <th>Fix on</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((i) => {
                          const open = () => (i.orderId ? onOpenOrder(i.orderId) : onOpenPerson(i.personId))
                          return (
                            <tr key={`${i.personId}:${i.issue}`} tabIndex={0} onClick={open} onKeyDown={(e) => e.key === 'Enter' && open()}>
                              <td>
                                <strong>{i.name}</strong>
                                {i.email && <div className="muted sub-line">{i.email}</div>}
                              </td>
                              <td>{i.label}</td>
                              <td className="r num">{i.days ?? '—'}</td>
                              <td className="muted">{i.orderId ? 'Order' : 'Person'}</td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </Panel>
            )
          })}
          <p className="footnote">{q.data.people} buyers looked at.</p>
        </>
      )}
    </>
  )
}
